import React, { useState, useRef } from "react";
import { Card, Result, Typography, Space, Tag, Table } from "antd";
import { isEmpty } from "lodash";
import { useLeaveStore } from "../../../../config/stores";
import dayjs from "dayjs";
import AppButton from "../../../../components/common/AppButton";
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { AiOutlinePrinter } from "react-icons/ai";
import { useReactToPrint } from "react-to-print";
import LeaveActionButtons from "./LeaveActionButtons";
import LeaveApprovalModal from "./LeaveApprovalModal";
import appConfig from "../../../../config/config.json";
import logo from "../../../../assets/logo/authLogo.png";

const { Title, Text } = Typography;

function resolveStaticFileUrl(path) {
  if (!path || typeof path !== "string") return null;
  const p = path.trim();
  if (!p) return null;
  if (/^https?:\/\//i.test(p)) return p;
  const base = String(appConfig.server_url || "").replace(/\/$/, "");
  return `${base}${p.startsWith("/") ? p : `/${p}`}`;
}

function formatPersonNameForPrint(p) {
  if (!p) return "";
  return `${p.first_name || ""} ${p.other_names || ""}`.trim();
}

/** Only rendered when `person.signature` resolves to a URL. */
function LeavePrintSignatureBlock({ label, person }) {
  const src = resolveStaticFileUrl(person?.signature);
  if (!src) return null;
  const name = formatPersonNameForPrint(person);
  return (
    <div style={{ minWidth: 140, maxWidth: 220, flex: "1 1 140px" }}>
      <Text strong>{label}</Text>
      <div style={{ fontSize: 11, marginTop: 4 }}>{name || "—"}</div>
      <div
        style={{
          marginTop: 8,
          minHeight: 52,
          border: "1px solid #d9d9d9",
          padding: 6,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={src}
          alt=""
          style={{ maxWidth: "100%", maxHeight: 80, objectFit: "contain" }}
        />
      </div>
    </div>
  );
}

function formatLeaveTypeLabel(leaveType) {
  if (leaveType == null || leaveType === "") return null;
  const s = String(leaveType).trim();
  if (!s) return null;
  return s.charAt(0) + s.slice(1).toLowerCase();
}

function normalizeWorkflowStatus(status) {
  if (status == null || status === "") return "";
  return String(status).toUpperCase().replace(/\s+/g, "_");
}

function getApprovalStepPresentation(step, rawStatus) {
  const s = normalizeWorkflowStatus(rawStatus);
  const checked = ["SUPERVISOR_APPROVED", "HR_APPROVED", "DIRECTOR_APPROVED", "DIRECTOR_REJECTED"];

  if (step === "check") {
    if (s === "SUBMITTED_TO_SUPERVISOR") return { text: "Pending", color: "warning" };
    if (s === "SUPERVISOR_REJECTED") return { text: "Rejected", color: "error" };
    if (checked.includes(s)) return { text: "Checked", color: "success" };
    return { text: "-", color: "default" };
  }

  if (step === "approve") {
    if (s === "DIRECTOR_APPROVED") return { text: "Approved", color: "success" };
    if (s === "DIRECTOR_REJECTED") return { text: "Rejected", color: "error" };
    if (s === "SUPERVISOR_APPROVED" || s === "HR_APPROVED") return { text: "Pending", color: "warning" };
    return { text: "-", color: "default" };
  }

  return { text: "-", color: "default" };
}

const LeaveApplicationDetails = () => {
  const leaveStore = useLeaveStore();
  const {
    selectedApplication,
    gettingSelectedApplication,
    selectedApplicationError,
  } = leaveStore ?? {};
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [currentAction, setCurrentAction] = useState({ action: null });

  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    contentRef: printRef,
    documentTitle:
      (
        `Leave application — ${selectedApplication?.applicant?.first_name ?? ""} ${
          selectedApplication?.applicant?.other_names ?? ""
        }`
      ).trim() || "Leave application",
    pageStyle: `
      @page { size: A4 portrait; margin: 15mm; }
      @media print {
        body { font-size: 11px !important; }
        table { width: 100% !important; border-collapse: collapse !important; }
        th, td { padding: 4px 6px !important; font-size: 11px !important; border: 1px solid #ccc !important; }
        .ant-tag { background: none !important; border: none !important; color: #000 !important; font-weight: bold; padding: 0 !important; }
        .ant-tag .anticon { display: none !important; }
        img { max-width: 150px !important; }
        h5 { font-size: 12px !important; margin: 8px 0 4px !important; }
        p { margin: 2px 0 !important; }
      }
    `,
  });

  const handleBack = () => {
    leaveStore.setShowLeaveApplicationDetails(false);
    leaveStore.setSelectedApplication(null);
  };

  const handleReload = () => {
    if (selectedApplication?.id) {
      leaveStore.getLeaveApplicationDetails(selectedApplication.id);
    }
  };

  const handleActionClick = (action) => {
    const id = selectedApplication?.id;
    if (!id) return;
    if (action === "submit") {
      leaveStore.submitToSupervisor(id);
    } else if (action === "undo-approve") {
      leaveStore.undoApproveLeaveApplication(id);
    } else {
      setCurrentAction({ action });
      setApprovalModalVisible(true);
    }
  };

  const handleApprovalSubmit = async (values) => {
    const { action } = currentAction;
    const comment = (values?.comment ?? "").trim();
    const id = selectedApplication?.id;
    if (!id) return;

    if (action === "check") {
      await leaveStore.checkLeaveApplication(id, { comment });
    } else if (action === "approve") {
      await leaveStore.approveLeaveApplication(id, { comment });
    } else if (action === "reject") {
      await leaveStore.rejectLeaveApplication(id, { comment });
    }
    setApprovalModalVisible(false);
  };

  const handleEdit = () => {
    leaveStore.setAddingLeaveApplication(true, true, selectedApplication);
  };

  const handleDelete = () => {
    if (selectedApplication?.id) leaveStore.deleteLeaveApplication(selectedApplication.id);
  };

  const approvalActionLoading =
    leaveStore.checkingLeaveApplication ||
    leaveStore.rejectingLeaveApplication ||
    leaveStore.approvingLeaveApplication;

  if (selectedApplicationError?.message && !gettingSelectedApplication) {
    return (
      <Result
        status="error"
        title="Unable to Get Leave Application Details"
        subTitle={
          selectedApplicationError.message ||
          "The requested leave application details could not be found"
        }
        extra={[
          <AppButton
            key="reload"
            type="default"
            onClick={handleReload}
            text="Retry"
            iconBefore={<ReloadOutlined />}
          />,
          <AppButton
            key="back"
            type="primary"
            onClick={handleBack}
            text="Back to Applications"
            iconBefore={<ArrowLeftOutlined />}
          />,
        ]}
      />
    );
  }

  if (
    !gettingSelectedApplication &&
    (isEmpty(selectedApplication) || !selectedApplication?.id)
  ) {
    return (
      <Result
        status="warning"
        title="No Leave Application Found"
        subTitle="The requested leave application details could not be found"
        extra={
          <AppButton
            type="primary"
            onClick={handleBack}
            text="Back to Applications"
            iconBefore={<ArrowLeftOutlined />}
          />
        }
      />
    );
  }

  const personalInfoColumns = [
    { title: "Field", dataIndex: "field", key: "field", width: "30%" },
    { title: "Value", dataIndex: "value", key: "value" },
  ];

  const personalInfoData = [
    {
      key: "1",
      field: "Name",
      value: (() => {
        const n = `${selectedApplication?.applicant?.first_name ?? ""} ${
          selectedApplication?.applicant?.other_names ?? ""
        }`.trim();
        return n || "-";
      })(),
    },
   
    {
      key: "5",
      field: "Address While on Leave",
      value: selectedApplication?.address_while_on_leave || "-",
    },
    {
      key: "6",
      field: "Tel/Mobile No",
      value: selectedApplication?.phone_while_on_leave || "-",
    },
    {
      key: "7",
      field: "Handed Over To",
      value: selectedApplication?.handed_over_to
        ? `${selectedApplication.handed_over_to.first_name ?? ""} ${
            selectedApplication.handed_over_to.other_names ?? ""
          }`.trim() || "-"
        : "-",
    },
  ];

  const leaveDetailsColumns = [
    { title: "Start Date", dataIndex: "start_date", key: "start_date" },
    { title: "End Date", dataIndex: "end_date", key: "end_date" },
    {
      title: "Number of Days",
      dataIndex: "number_of_days",
      key: "number_of_days",
    },
    { title: "Leave Type", dataIndex: "leave_type", key: "leave_type" },
  ];

  const leaveTypeLabel = formatLeaveTypeLabel(selectedApplication?.leave_type);

  const leaveDetailsData = [
    {
      key: "1",
      start_date: selectedApplication?.start_date
        ? dayjs(selectedApplication.start_date).format("DD/MM/YYYY")
        : "-",
      end_date: selectedApplication?.end_date
        ? dayjs(selectedApplication.end_date).format("DD/MM/YYYY")
        : "-",
      number_of_days: selectedApplication?.number_of_days ?? "-",
      leave_type: (
        <Space>
          <Tag color={leaveTypeLabel ? "blue" : "default"}>
            {leaveTypeLabel ?? "-"}
          </Tag>
          {selectedApplication?.leave_type === "OTHER" && (
            <Text type="secondary">
              ({selectedApplication?.other_leave_type ?? "-"})
            </Text>
          )}
        </Space>
      ),
    },
  ];

  const eligibilityColumns = [
    { title: "Total Days Accrued", dataIndex: "accrued", key: "accrued" },
    { title: "Total Days Available", dataIndex: "available", key: "available" },
    { title: "Days To Be Taken", dataIndex: "to_be_taken", key: "to_be_taken" },
    { title: "Balance After Leave", dataIndex: "balance", key: "balance" },
  ];

  const eligibilityData = [
    {
      key: "1",
      accrued: selectedApplication?.total_leave_days_accrued ?? "-",
      available: selectedApplication?.total_leave_days_available ?? "-",
      to_be_taken: selectedApplication?.total_leave_days_to_be_taken ?? "-",
      balance: selectedApplication?.balance_after_leave ?? "-",
    },
  ];

  const approvalColumns = [
    {
      title: "Approver",
      dataIndex: "approver",
      key: "approver",
      width: "20%",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "20%",
      render: (text, record) => (
        <Tag
          color={
            record.statusColor === "success"
              ? "success"
              : record.statusColor === "error"
              ? "error"
              : record.statusColor === "processing"
              ? "processing"
              : record.statusColor === "default"
              ? "default"
              : "warning"
          }
        >
          {text}
        </Tag>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "20%",
    },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
      width: "25%",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: "15%",
    },
  ];

  const chk = getApprovalStepPresentation("check", selectedApplication?.status);
  const apr = getApprovalStepPresentation("approve", selectedApplication?.status);
  const personName = (p) =>
    p ? `${p.first_name ?? ""} ${p.other_names ?? ""}`.trim() || "-" : "-";
  const actionDate = (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "-");

  const approvalData = [
    {
      key: "1",
      approver: "Checked By",
      status: chk.text,
      statusColor: chk.color,
      name: personName(selectedApplication?.supervisor),
      comment: selectedApplication?.supervisor_comment || "No comment",
      date: actionDate(selectedApplication?.supervisor_action_date),
    },
    ...(selectedApplication?.hr
      ? [
          {
            key: "hr",
            approver: "Human Resource Officer",
            status: "Checked",
            statusColor: "success",
            name: personName(selectedApplication.hr),
            comment: selectedApplication?.hr_comment || "No comment",
            date: actionDate(selectedApplication?.hr_action_date),
          },
        ]
      : []),
    {
      key: "2",
      approver: "Approved By",
      status: apr.text,
      statusColor: apr.color,
      name: personName(selectedApplication?.director),
      comment: selectedApplication?.director_comment || "No comment",
      date: actionDate(selectedApplication?.director_action_date),
    },
  ];

  const leaveSignatureRows = [
    { key: "applicant", label: "Applicant", person: selectedApplication?.applicant },
    { key: "checker", label: "Checked By", person: selectedApplication?.supervisor },
    { key: "hr", label: "Human Resource Officer", person: selectedApplication?.hr },
    { key: "approver", label: "Approved By", person: selectedApplication?.director },
  ].filter((row) => resolveStaticFileUrl(row.person?.signature));

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        loading={leaveStore.gettingSelectedApplication}
        title={
          <Space direction="vertical" size={4}>
            <p className="fw-bold mb-0">
              AMANI INITIATIVE STAFF LEAVE APPLICATION FORM
            </p>
          </Space>
        }
        extra={
          <Space>
            <LeaveActionButtons
              application={selectedApplication}
              onActionClick={handleActionClick}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
            <AppButton
              iconBefore={<AiOutlinePrinter />}
              text="Download"
              type="default"
              onClick={() => handlePrint()}
            />
            <AppButton
              type="default"
              onClick={handleReload}
              iconBefore={<ReloadOutlined />}
              text="Reload"
              loading={leaveStore.gettingSelectedApplication}
            />
            <AppButton
              type="primary"
              danger
              onClick={handleBack}
              iconBefore={<CloseOutlined />}
              text="Close"
            />
          </Space>
        }
      >
        <div ref={printRef}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <img src={logo} alt="Amani Initiative" style={{ height: 60, marginBottom: 8 }} />
            <p style={{ margin: 0, fontSize: 12 }}>P.O.BOX 610029, Maracha, Uganda | Adongoro Cell, Maracha Town Council</p>
            <p style={{ margin: 0, fontSize: 12 }}>Mob: +256 393102379</p>
            <p style={{ margin: 0, fontSize: 12 }}>Web: www.amaniinitiative.org | Email: info@amaniinitiative.org</p>
            <Title level={5} style={{ marginTop: 12, marginBottom: 0 }}>
              AMANI INITIATIVE STAFF LEAVE APPLICATION FORM
            </Title>
          </div>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <div>
              <Title level={5}>Section 1: PERSONAL INFORMATION</Title>
              <Table
                columns={personalInfoColumns}
                dataSource={personalInfoData}
                pagination={false}
                size="small"
              />
            </div>

            <div>
              <Title level={5}>Section 2: LEAVE DETAILS</Title>
              <Table
                columns={leaveDetailsColumns}
                dataSource={leaveDetailsData}
                pagination={false}
                size="small"
              />
            </div>

            <div>
              <Title level={5}>
                Section 3: ELIGIBILITY TO GUIDE LEAVE APPROVAL PROCESS
              </Title>
              <Table
                columns={eligibilityColumns}
                dataSource={eligibilityData}
                pagination={false}
                size="small"
              />
            </div>

            <div>
              <Title level={5}>Section 4: LEAVE APPROVAL/REJECTION</Title>
              <Table
                columns={approvalColumns}
                dataSource={approvalData}
                pagination={false}
                size="small"
              />
            </div>

            {leaveSignatureRows.length > 0 ? (
              <div style={{ pageBreakInside: "avoid" }}>
                <Title level={5}>Section 5: SIGNATURES</Title>
                <div
                  className="d-flex flex-wrap"
                  style={{ gap: 20, alignItems: "flex-start" }}
                >
                  {leaveSignatureRows.map((row) => (
                    <LeavePrintSignatureBlock
                      key={row.key}
                      label={row.label}
                      person={row.person}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </Space>
        </div>
      </Card>
      <LeaveApprovalModal
        visible={approvalModalVisible}
        onCancel={() => setApprovalModalVisible(false)}
        onSubmit={handleApprovalSubmit}
        loading={approvalActionLoading}
        action={currentAction.action}
      />
    </Space>
  );
};

export default LeaveApplicationDetails;
